import { memo } from "react";
import {
    IconPlayerPlayFilled,
    IconPlayerPauseFilled,
    IconCheck,
    IconChecks,
    IconBulb,
    IconBulbOff,
    IconPictureInPicture,
    IconPictureInPictureOff,
    IconRectangle,
    IconRectangleFilled,
    IconMaximize,
    IconMinimize,
} from "@tabler/icons-react";

import ControlButton from "./ControlButton";
import PlaylistNavButton from "./PlaylistNavButton";
import VolumeControl from "./VolumeControl";
import TimeDisplay from "./TimeDisplay";
import SpeedMenu from "./SpeedMenu";
import AutoplaySwitch from "./AutoplaySwitch";

/**
 * ControlBar — the full set of custom controls, grouped in translucent pills.
 * Layout-agnostic: the horizontal bar and the vertical rail are pure CSS on
 * `.player-controls-row`.
 */
function ControlBar({
    videoRef,
    state,
    actions,
    watched,
    onToggleWatched,
    prevContent,
    nextContent,
    onNavigate,
    autoplayEnabled,
    onAutoplayChange,
    single,
    vertical = false,
}) {
    const {
        isPlaying, volume, muted, speed,
        isFullscreen, isPip, pipSupported, isTheater, isAmbient,
    } = state;

    return (
        <div className="player-controls-row">
            <div className="player-cluster player-cluster--main">
                <div className="player-pill">
                    <ControlButton
                        label={isPlaying ? "Pausar (espaço)" : "Reproduzir (espaço)"}
                        onClick={actions.togglePlay}
                    >
                        {isPlaying ? <IconPlayerPauseFilled /> : <IconPlayerPlayFilled />}
                    </ControlButton>
                </div>

                {!single && (
                    <div className="player-pill">
                        <PlaylistNavButton direction="prev" content={prevContent} onNavigate={onNavigate} />
                        <PlaylistNavButton direction="next" content={nextContent} onNavigate={onNavigate} />
                    </div>
                )}

                <div className="player-pill">
                    <VolumeControl
                        volume={volume}
                        muted={muted}
                        onChangeVolume={actions.changeVolume}
                        onToggleMute={actions.toggleMute}
                        vertical={vertical}
                    />
                </div>

                <TimeDisplay videoRef={videoRef} />
            </div>

            <div className="player-cluster player-cluster--settings">
                <div className="player-pill">
                    {!single && <AutoplaySwitch enabled={autoplayEnabled} onChange={onAutoplayChange} />}

                    <SpeedMenu speed={speed} onSetSpeed={actions.setSpeed} />

                    <ControlButton
                        label={watched ? "Marcar como não assistido" : "Marcar como assistido"}
                        onClick={onToggleWatched}
                        active={watched}
                        className="player-invert-active"
                    >
                        {watched ? <IconChecks /> : <IconCheck />}
                    </ControlButton>

                    <ControlButton
                        label={isAmbient ? "Desativar iluminação cinematográfica" : "Ativar iluminação cinematográfica"}
                        onClick={actions.toggleAmbient}
                        active={isAmbient}
                        className="player-invert-active"
                    >
                        {isAmbient ? <IconBulb /> : <IconBulbOff />}
                    </ControlButton>

                    {pipSupported && (
                        <ControlButton label="Picture-in-picture" onClick={actions.togglePip} active={isPip}>
                            {isPip ? <IconPictureInPictureOff /> : <IconPictureInPicture />}
                        </ControlButton>
                    )}

                    <ControlButton
                        label={isTheater ? "Sair do modo teatro (t)" : "Modo teatro (t)"}
                        onClick={actions.toggleTheater}
                        active={isTheater}
                        className="player-theater-button"
                    >
                        {isTheater ? <IconRectangleFilled /> : <IconRectangle />}
                    </ControlButton>

                    <ControlButton
                        label={isFullscreen ? "Sair do ecrã inteiro (f)" : "Ecrã inteiro (f)"}
                        onClick={actions.toggleFullscreen}
                    >
                        {isFullscreen ? <IconMinimize /> : <IconMaximize />}
                    </ControlButton>
                </div>
            </div>
        </div>
    );
}

export default memo(ControlBar);
